/**
 * @fileoverview Free and Grow More (the paid plan; Grow is the app) side by side, with
 * ticks. The rows are the server's (the billing status's plans.rows; services/
 * billing/planRules on the server decides each cell from the rule that
 * enforces it, as the owner and marketing agreed the list on 7 October
 * 2026), and so are Free's plants (plans.free, from the Grow settings), so
 * the column says what Free really has. A cell is a tick (true), a dash
 * (false) or words (billing.compare.values.<key>, which may name Free's
 * plants). A status with no rows draws no table.
 *
 * Watch out: the AI row is a plain tick, with no allowance words and no Fair
 * use link (owner, O16, 2026-10-08). The allowance is told in the terms and
 * in the app's own notes (80%, limit, "Photo help this month").
 *
 * Where: the subscribe page, under the plan lines, for anyone not subscribed
 * or paused.
 * API: none; the rows and Free's plants come in as props (the billing
 * status's plans.rows and plans.free).
 * Words (en/es): billing.compare.title, .titleBoth, .free, .grow, .yes, .no,
 * .rows.*, .values.*.
 *
 * @module PlanComparison
 */

import React from 'react';
import { Check, Minus } from 'lucide-react';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { cn } from '@/lib/utils';
import type { PlanCell, PlanRow } from '@/shared/api/billing';

/** Free's numbers, for the words that name them. */
interface FreeNumbers {
  /** Up to how many plants Free has. */
  plants: number;
}

/** What the comparison draws. */
interface PlanComparisonProps {
  /** The rows, as the server sends them (plans.rows). */
  rows: readonly PlanRow[];
  /** Free's numbers as the server sends them (plans.free). */
  free: FreeNumbers;
  /** Extra classes for the outer block. */
  className?: string;
}

/**
 * One cell: a tick, a dash, or words (from billing.compare.values, with Free's numbers).
 *
 * @param props.cell - The cell.
 * @param props.free - Free's plants, for words that name them.
 */
const Cell: React.FC<{ cell: PlanCell; free: FreeNumbers }> = ({ cell, free }) => {
  const { reactTranslate: t } = useTranslation();
  if (cell === true) {
    return (
      <>
        <Check className="mx-auto h-4 w-4 text-primary" aria-hidden="true" />
        <span className="sr-only">{t('billing.compare.yes')}</span>
      </>
    );
  }
  if (cell === false) {
    return (
      <>
        <Minus className="mx-auto h-4 w-4 text-muted-foreground" aria-hidden="true" />
        <span className="sr-only">{t('billing.compare.no')}</span>
      </>
    );
  }
  return <span>{t(`billing.compare.values.${cell}`, { plants: free.plants })}</span>;
};

/**
 * The comparison table.
 *
 * @param props.rows - The server's rows.
 * @param props.free - Free's numbers as the server sends them (plans.free).
 * @param props.className - Extra classes for the outer block.
 */
export const PlanComparison: React.FC<PlanComparisonProps> = ({ rows, free, className }) => {
  const { reactTranslate: t } = useTranslation();
  if (rows.length === 0) return null;
  const heading = 'font-semibold font-body';
  return (
    <section className={cn('rounded-sm border bg-card', className)} aria-labelledby="plan-comparison-title">
      <h2 id="plan-comparison-title" className={cn(heading, 'px-4 pt-4 text-lg')}>
        {t('billing.compare.titleBoth')}
      </h2>
      <table className="mt-2 w-full text-sm">
        <thead>
          <tr className="border-b text-left text-xs text-muted-foreground">
            <th scope="col" className="px-4 py-2 font-normal"><span className="sr-only">{t('billing.compare.title')}</span></th>
            <th scope="col" className="w-24 px-2 py-2 text-center font-medium">{t('billing.compare.free')}</th>
            <th scope="col" className="w-28 px-2 py-2 text-center font-medium text-foreground sm:w-36">{t('billing.compare.grow')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key} className="border-b last:border-b-0">
              <th scope="row" className="px-4 py-2 text-left font-normal">{t(`billing.compare.rows.${row.key}`)}</th>
              <td className="px-2 py-2 text-center text-muted-foreground"><Cell cell={row.free} free={free} /></td>
              <td className="px-2 py-2 text-center"><Cell cell={row.grow} free={free} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
};

export default PlanComparison;
